

import { useState, useEffect, useRef } from "react"
import "../styles/LCSVisualizer.css"
import Button from "../../shared/Button"

const LCSVisualizer = () => {
  const [str1, setStr1] = useState("abcde")
  const [str2, setStr2] = useState("ace")
  const [matrix, setMatrix] = useState([])
  const [currentStep, setCurrentStep] = useState(0)
  const [tableSteps, setTableSteps] = useState([])
  const [pathCells, setPathCells] = useState([])
  const [lcsCells, setLcsCells] = useState([])
  const [lcsString, setLcsString] = useState("")
  const [isPlaying, setIsPlaying] = useState(false)
  const [speed, setSpeed] = useState(500)
  const [activeTab, setActiveTab] = useState("visualize")
  const [explanation, setExplanation] = useState("")
  const [showPopup, setShowPopup] = useState(false)
  const [popupContent, setPopupContent] = useState({ title: "", content: "" })
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 })
  const [animationFrame, setAnimationFrame] = useState(0)
  const [arrows, setArrows] = useState([])
  const [currentStepInfo, setCurrentStepInfo] = useState(null)
  const [pathStepInfo, setPathStepInfo] = useState(null)

  const tableRef = useRef(null)
  const animationRef = useRef(null)
  const tableContainerRef = useRef(null)

  useEffect(() => {
    calculateLCS()
  }, [str1, str2])

  useEffect(() => {
    let timer
    if (isPlaying && currentStep < tableSteps.length) {
      timer = setTimeout(() => {
        setCurrentStep((prev) => prev + 1)
        const step = tableSteps[currentStep]
        if (step) {
          const explanation = generateExplanation(step, matrix)
          setExplanation(explanation)
          setCurrentStepInfo(step)
        }
      }, speed)
    } else if (currentStep >= tableSteps.length && pathCells.length > 0 && isPlaying) {
      // Start tracing the path after table is filled
      tracePath()
    }
    return () => clearTimeout(timer)
  }, [isPlaying, currentStep, tableSteps, speed, matrix])

  useEffect(() => {
    // Animation for the Learn tab
    if (activeTab === "learn" && animationRef.current) {
      const animationInterval = setInterval(() => {
        setAnimationFrame((prev) => (prev + 1) % 6)
      }, 1500)

      return () => clearInterval(animationInterval)
    }
  }, [activeTab])

  const calculateLCS = () => {
    const m = str1.length
    const n = str2.length

    // Clear previous state
    setCurrentStep(0)
    setTableSteps([])
    setPathCells([])
    setLcsCells([])
    setArrows([])
    setExplanation("")
    setCurrentStepInfo(null)
    setPathStepInfo(null)

    // Initialize the dp array
    const dp = Array(m + 1)
      .fill(null)
      .map(() => Array(n + 1).fill(0))
    setMatrix(dp)

    // Collect steps for visualization
    const steps = []

    // Fill the dp table
    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        if (str1[i - 1] === str2[j - 1]) {
          dp[i][j] = dp[i - 1][j - 1] + 1
          steps.push({
            i,
            j,
            value: dp[i][j],
            match: true,
            char1: str1[i - 1],
            char2: str2[j - 1],
            prevValue: dp[i - 1][j - 1],
            leftValue: dp[i][j - 1],
            topValue: dp[i - 1][j],
            direction: "diagonal",
          })
        } else {
          dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1])
          steps.push({
            i,
            j,
            value: dp[i][j],
            match: false,
            char1: str1[i - 1],
            char2: str2[j - 1],
            prevValue: dp[i - 1][j - 1],
            leftValue: dp[i][j - 1],
            topValue: dp[i - 1][j],
            direction: dp[i - 1][j] > dp[i][j - 1] ? "up" : "left",
          })
        }
      }
    }

    setTableSteps(steps)
    setMatrix(dp)

    // Calculate path and LCS
    if (m > 0 && n > 0) {
      const { path, lcs, lcsCells: lcs_cells, arrows: pathArrows } = findLCSPath(dp, m, n)
      setPathCells(path)
      setLcsCells(lcs_cells)
      setLcsString(lcs)
      setArrows(pathArrows)
    } else {
      setLcsString("")
    }
  }

  const generateExplanation = (step, matrix) => {
    const { i, j, match, char1, char2, value, prevValue, leftValue, topValue } = step

    if (match) {
      return `Step ${currentStep + 1}: Comparing "${char1}" and "${char2}" - MATCH! Cell (${i},${j}) = diagonal cell (${i - 1},${j - 1}) + 1 = ${prevValue} + 1 = ${value}`
    } else {
      const source = leftValue > topValue ? "left" : topValue > leftValue ? "top" : "both left and top"
      const sourceValue = Math.max(leftValue, topValue)
      return `Step ${currentStep + 1}: Comparing "${char1}" and "${char2}" - NO MATCH. Cell (${i},${j}) = max(top cell (${i - 1},${j}) = ${topValue}, left cell (${i},${j - 1}) = ${leftValue}) = ${value} (from ${source})`
    }
  }

  const findLCSPath = (dp, m, n) => {
    let i = m,
      j = n
    const path = []
    const lcsCells = []
    const arrows = []
    let lcs = ""

    while (i > 0 && j > 0) {
      if (str1[i - 1] === str2[j - 1]) {
        path.push([i, j])
        lcsCells.push([i, j])
        arrows.push({ from: [i, j], to: [i - 1, j - 1], type: "diagonal" })
        lcs = str1[i - 1] + lcs
        i--
        j--
      } else if (dp[i - 1][j] > dp[i][j - 1]) {
        path.push([i, j])
        arrows.push({ from: [i, j], to: [i - 1, j], type: "up" })
        i--
      } else {
        path.push([i, j])
        arrows.push({ from: [i, j], to: [i, j - 1], type: "left" })
        j--
      }
    }

    return {
      path: path.reverse(),
      lcs,
      lcsCells: lcsCells.reverse(),
      arrows: arrows.reverse(),
    }
  }

  const tracePath = () => {
    setIsPlaying(false)

    // Reset path visualization
    setPathCells([])
    setLcsCells([])
    setArrows([])

    // Animate path tracing
    const animatePath = (index = 0) => {
      if (index < pathCells.length) {
        const [i, j] = pathCells[index]
        setPathCells((prev) => [...prev.slice(0, index), pathCells[index]])

        // Add arrow if not the last cell
        if (index < arrows.length) {
          setArrows((prev) => [...prev.slice(0, index), arrows[index]])
        }

        // Check if this cell is part of the LCS
        const isLcsCell = lcsCells.some(([x, y]) => x === i && y === j)

        if (isLcsCell) {
          setLcsCells((prev) => [...prev, pathCells[index]])
          const char = str1[i - 1]
          setExplanation(
            `Characters match: "${str1[i - 1]}" = "${str2[j - 1]}". Adding "${char}" to LCS and moving diagonally to (${i - 1}, ${j - 1}).`,
          )

          // Update path step info for the sidebar
          setPathStepInfo({
            type: "lcs",
            i,
            j,
            char,
            lcsIndex: lcsCells.length,
            currentLcs: lcsString.substring(0, lcsCells.length),
          })
        } else {
          // Determine direction of movement
          const nextIndex = index + 1
          if (nextIndex < pathCells.length) {
            const [nextI, nextJ] = pathCells[nextIndex]
            if (nextI < i && nextJ === j) {
              setExplanation(`Characters don't match. Value from top (${i - 1}, ${j}) is greater, so moving up.`)
              setPathStepInfo({
                type: "move",
                i,
                j,
                direction: "up",
                reason: `Value from top (${i - 1}, ${j}) is greater`,
              })
            } else if (nextI === i && nextJ < j) {
              setExplanation(
                `Characters don't match. Value from left (${i}, ${j - 1}) is greater or equal, so moving left.`,
              )
              setPathStepInfo({
                type: "move",
                i,
                j,
                direction: "left",
                reason: `Value from left (${i}, ${j - 1}) is greater or equal`,
              })
            }
          }
        }

        setTimeout(() => animatePath(index + 1), speed)
      } else {
        setExplanation(
          `LCS tracing complete. The longest common subsequence is "${lcsString}" with length ${lcsString.length}.`,
        )

        // Final result in sidebar
        setPathStepInfo({
          type: "complete",
          lcs: lcsString,
          length: lcsString.length,
        })
      }
    }

    setTimeout(() => animatePath(), 500)
  }

  const resetVisualization = () => {
    setCurrentStep(0)
    setPathCells([])
    setLcsCells([])
    setArrows([])
    setIsPlaying(false)
    setExplanation("")
    setCurrentStepInfo(null)
    setPathStepInfo(null)
  }

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying)
  }

  const setSpeedMultiplier = (multiplier) => {
    const baseSpeed = 500
    setSpeed(baseSpeed / multiplier)
  }

  const getCellClass = (i, j) => {
    // Current step in table filling
    const isCurrentStep = tableSteps[currentStep - 1]?.i === i && tableSteps[currentStep - 1]?.j === j

    // Check if cell is part of the path
    const isPathCell = pathCells.some(([x, y]) => x === i && y === j)

    // Check if cell is part of the LCS
    const isLcsCell = lcsCells.some(([x, y]) => x === i && y === j)

    // Find the arrow for this cell
    const arrow = arrows.find((arrow) => arrow.from[0] === i && arrow.from[1] === j)
    let arrowClass = ""

    if (arrow) {
      if (arrow.type === "diagonal") arrowClass = "diagonal-indicator"
      else if (arrow.type === "up") arrowClass = "up-indicator"
      else if (arrow.type === "left") arrowClass = "left-indicator"
    }

    if (isCurrentStep) return `current-step ${arrowClass}`
    if (isLcsCell) return `lcs-cell ${arrowClass}`
    if (isPathCell) return `path-cell ${arrowClass}`
    return arrowClass
  }

  const handleCellClick = (i, j, event) => {
    // Find the step that corresponds to this cell
    const step = tableSteps.find((step) => step.i === i && step.j === j)

    if (step) {
      setCurrentStepInfo(step)
    }

    // Check if this cell is part of the LCS path
    const isLcsCell = lcsCells.some(([x, y]) => x === i && y === j)
    if (isLcsCell) {
      const lcsIndex = lcsCells.findIndex(([x, y]) => x === i && y === j)
      const char = str1[i - 1]

      setPathStepInfo({
        type: "lcs",
        i,
        j,
        char,
        lcsIndex: lcsIndex + 1,
        currentLcs: lcsString.substring(0, lcsIndex + 1),
      })
    }
  }

  const getOrdinalSuffix = (i) => {
    const j = i % 10,
      k = i % 100
    if (j === 1 && k !== 11) {
      return "st"
    }
    if (j === 2 && k !== 12) {
      return "nd"
    }
    if (j === 3 && k !== 13) {
      return "rd"
    }
    return "th"
  }

  const closePopup = () => {
    setShowPopup(false)
  }

  // Animation frames for the Learn tab
  const renderAnimationFrame = () => {
    const demoStr1 = "abcde"
    const demoStr2 = "ace"

    // Simple animation showing the LCS process
    switch (animationFrame) {
      case 0:
        return (
          <div className="animation-frame">
            <div className="animation-text">Starting with two strings:</div>
            <div className="animation-strings">
              <div className="string-animation">
                String 1:
                <span className="char-animate">a</span>
                <span className="char-animate" style={{ animationDelay: "0.2s" }}>
                  b
                </span>
                <span className="char-animate" style={{ animationDelay: "0.4s" }}>
                  c
                </span>
                <span className="char-animate" style={{ animationDelay: "0.6s" }}>
                  d
                </span>
                <span className="char-animate" style={{ animationDelay: "0.8s" }}>
                  e
                </span>
              </div>
              <div className="string-animation">
                String 2:
                <span className="char-animate">a</span>
                <span className="char-animate" style={{ animationDelay: "0.2s" }}>
                  c
                </span>
                <span className="char-animate" style={{ animationDelay: "0.4s" }}>
                  e
                </span>
              </div>
            </div>
          </div>
        )
      case 1:
        return (
          <div className="animation-frame">
            <div className="animation-text">Create a table with dimensions (m+1) × (n+1)</div>
            <div className="animation-table small">
              <table className="animated-table">
                <tbody>
                  <tr>
                    <td></td>
                    <td></td>
                    <td>a</td>
                    <td>c</td>
                    <td>e</td>
                  </tr>
                  <tr>
                    <td></td>
                    <td className="cell-animate">0</td>
                    <td className="cell-animate" style={{ animationDelay: "0.1s" }}>
                      0
                    </td>
                    <td className="cell-animate" style={{ animationDelay: "0.2s" }}>
                      0
                    </td>
                    <td className="cell-animate" style={{ animationDelay: "0.3s" }}>
                      0
                    </td>
                  </tr>
                  <tr>
                    <td>a</td>
                    <td className="cell-animate" style={{ animationDelay: "0.4s" }}>
                      0
                    </td>
                    <td className="cell-animate" style={{ animationDelay: "0.5s" }}>
                      ?
                    </td>
                    <td className="cell-animate" style={{ animationDelay: "0.6s" }}>
                      ?
                    </td>
                    <td className="cell-animate" style={{ animationDelay: "0.7s" }}>
                      ?
                    </td>
                  </tr>
                  <tr>
                    <td>b</td>
                    <td className="cell-animate" style={{ animationDelay: "0.8s" }}>
                      0
                    </td>
                    <td className="cell-animate" style={{ animationDelay: "0.9s" }}>
                      ?
                    </td>
                    <td className="cell-animate" style={{ animationDelay: "1.0s" }}>
                      ?
                    </td>
                    <td className="cell-animate" style={{ animationDelay: "1.1s" }}>
                      ?
                    </td>
                  </tr>
                  <tr>
                    <td>c</td>
                    <td className="cell-animate" style={{ animationDelay: "1.2s" }}>
                      0
                    </td>
                    <td className="cell-animate" style={{ animationDelay: "1.3s" }}>
                      ?
                    </td>
                    <td className="cell-animate" style={{ animationDelay: "1.4s" }}>
                      ?
                    </td>
                    <td className="cell-animate" style={{ animationDelay: "1.5s" }}>
                      ?
                    </td>
                  </tr>
                  <tr>
                    <td>d</td>
                    <td className="cell-animate" style={{ animationDelay: "1.6s" }}>
                      0
                    </td>
                    <td className="cell-animate" style={{ animationDelay: "1.7s" }}>
                      ?
                    </td>
                    <td className="cell-animate" style={{ animationDelay: "1.8s" }}>
                      ?
                    </td>
                    <td className="cell-animate" style={{ animationDelay: "1.9s" }}>
                      ?
                    </td>
                  </tr>
                  <tr>
                    <td>e</td>
                    <td className="cell-animate" style={{ animationDelay: "2.0s" }}>
                      0
                    </td>
                    <td className="cell-animate" style={{ animationDelay: "2.1s" }}>
                      ?
                    </td>
                    <td className="cell-animate" style={{ animationDelay: "2.2s" }}>
                      ?
                    </td>
                    <td className="cell-animate" style={{ animationDelay: "2.3s" }}>
                      ?
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )
      case 2:
        return (
          <div className="animation-frame">
            <div className="animation-text">Fill the table using the recurrence relation:</div>
            <div className="animation-formula">
              <div className="formula-container">
                <div className="formula-line">
                  <span className="keyword">if</span> (str1[i-1] == str2[j-1])
                </div>
                <div className="formula-line indent">dp[i][j] = dp[i-1][j-1] + 1</div>
                <div className="formula-line">
                  <span className="keyword">else</span>
                </div>
                <div className="formula-line indent">
                  dp[i][j] = <span className="function">max</span>(dp[i-1][j], dp[i][j-1])
                </div>
              </div>
              <div className="formula-explanation">
                <div className="formula-case">
                  <div className="case-title">Case 1: Characters match</div>
                  <div className="case-arrow diagonal-arrow-anim">↖️ Take diagonal value + 1</div>
                </div>
                <div className="formula-case">
                  <div className="case-title">Case 2: Characters don't match</div>
                  <div className="case-arrow">
                    <span className="up-arrow-anim">⬆️</span> or <span className="left-arrow-anim">⬅️</span> Take max of
                    up or left
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      case 3:
        return (
          <div className="animation-frame">
            <div className="animation-text">Filled table:</div>
            <div className="animation-table small">
              <table className="animated-table filled">
                <tbody>
                  <tr>
                    <td></td>
                    <td></td>
                    <td>a</td>
                    <td>c</td>
                    <td>e</td>
                  </tr>
                  <tr>
                    <td></td>
                    <td>0</td>
                    <td>0</td>
                    <td>0</td>
                    <td>0</td>
                  </tr>
                  <tr>
                    <td>a</td>
                    <td>0</td>
                    <td className="highlight pulse">1</td>
                    <td>1</td>
                    <td>1</td>
                  </tr>
                  <tr>
                    <td>b</td>
                    <td>0</td>
                    <td>1</td>
                    <td>1</td>
                    <td>1</td>
                  </tr>
                  <tr>
                    <td>c</td>
                    <td>0</td>
                    <td>1</td>
                    <td className="highlight pulse">2</td>
                    <td>2</td>
                  </tr>
                  <tr>
                    <td>d</td>
                    <td>0</td>
                    <td>1</td>
                    <td>2</td>
                    <td>2</td>
                  </tr>
                  <tr>
                    <td>e</td>
                    <td>0</td>
                    <td>1</td>
                    <td>2</td>
                    <td className="highlight pulse">3</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )
      case 4:
        return (
          <div className="animation-frame">
            <div className="animation-text">Trace back from bottom-right to find LCS:</div>
            <div className="animation-table small">
              <table className="animated-table path">
                <tbody>
                  <tr>
                    <td></td>
                    <td></td>
                    <td>a</td>
                    <td>c</td>
                    <td>e</td>
                  </tr>
                  <tr>
                    <td></td>
                    <td>0</td>
                    <td>0</td>
                    <td>0</td>
                    <td>0</td>
                  </tr>
                  <tr>
                    <td>a</td>
                    <td>0</td>
                    <td className="path diagonal-animate">1</td>
                    <td>1</td>
                    <td>1</td>
                  </tr>
                  <tr>
                    <td>b</td>
                    <td>0</td>
                    <td>1</td>
                    <td>1</td>
                    <td>1</td>
                  </tr>
                  <tr>
                    <td>c</td>
                    <td>0</td>
                    <td>1</td>
                    <td className="path diagonal-animate">2</td>
                    <td>2</td>
                  </tr>
                  <tr>
                    <td>d</td>
                    <td>0</td>
                    <td>1</td>
                    <td>2</td>
                    <td>2</td>
                  </tr>
                  <tr>
                    <td>e</td>
                    <td>0</td>
                    <td>1</td>
                    <td>2</td>
                    <td className="path start-animate">3</td>
                  </tr>
                </tbody>
              </table>
              <div className="path-arrows">
                <div className="diagonal-arrow arrow1"></div>
                <div className="diagonal-arrow arrow2"></div>
                <div className="diagonal-arrow arrow3"></div>
              </div>
            </div>
          </div>
        )
      case 5:
        return (
          <div className="animation-frame">
            <div className="animation-text">Final LCS:</div>
            <div className="animation-result">
              <div className="string-compare">
                <div>
                  String 1: <span className="char-highlight">a</span>b<span className="char-highlight">c</span>d
                  <span className="char-highlight">e</span>
                </div>
                <div>
                  String 2: <span className="char-highlight">a</span>
                  <span className="char-highlight">c</span>
                  <span className="char-highlight">e</span>
                </div>
              </div>
              <div className="lcs-final">
                <span className="lcs-char-animate">a</span>
                <span className="lcs-char-animate" style={{ animationDelay: "0.5s" }}>
                  c
                </span>
                <span className="lcs-char-animate" style={{ animationDelay: "1s" }}>
                  e
                </span>
              </div>
              <div className="lcs-length">Length: 3</div>
            </div>
          </div>
        )
      default:
        return null
    }
  }

  // Render the current step details in the right sidebar
  const renderStepDetails = () => {
    if (!currentStepInfo) {
      return (
        <div className="no-step-info">
          <p>Click Play to start the visualization or click on any cell for details.</p>
        </div>
      )
    }

    const { i, j, match, char1, char2, value, prevValue, leftValue, topValue, direction } = currentStepInfo

    return (
      <div className="step-details">
        <h3>
          Cell ({i},{j}) Details
        </h3>

        <div className="detail-section">
          <h4>Character Comparison</h4>
          <div className="char-comparison">
            <span className={`char-box ${match ? "match" : "no-match"}`}>{char1}</span>
            <span className="comparison-symbol">{match ? "=" : "≠"}</span>
            <span className={`char-box ${match ? "match" : "no-match"}`}>{char2}</span>
          </div>
          <p className="match-result">{match ? "Characters match!" : "Characters don't match"}</p>
        </div>

        <div className="detail-section">
          <h4>Formula Applied</h4>
          <div className="formula-box">
            {match ? "dp[i][j] = dp[i-1][j-1] + 1" : "dp[i][j] = max(dp[i-1][j], dp[i][j-1])"}
          </div>
        </div>

        <div className="detail-section">
          <h4>Calculation</h4>
          <div className="calculation">
            {match
              ? `dp[${i}][${j}] = dp[${i - 1}][${j - 1}] + 1 = ${prevValue} + 1 = ${value}`
              : `dp[${i}][${j}] = max(${topValue}, ${leftValue}) = ${value}`}
          </div>
          {!match && (
            <div className="calculation-detail">
              {topValue > leftValue
                ? `Top value (${topValue}) is greater`
                : leftValue > topValue
                  ? `Left value (${leftValue}) is greater`
                  : `Values are equal, choosing left by convention`}
            </div>
          )}
        </div>

        <div className="detail-section">
          <h4>Next Move</h4>
          <div className="move-direction">
            {match ? (
              <>
                <span className="arrow diagonal">↖️</span> Move diagonally when characters match
              </>
            ) : topValue > leftValue ? (
              <>
                <span className="arrow up">⬆️</span> Move up when top value is greater
              </>
            ) : (
              <>
                <span className="arrow left">⬅️</span> Move left when left value is greater/equal
              </>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Render the path tracing details in the right sidebar
  const renderPathDetails = () => {
    if (!pathStepInfo) {
      return (
        <div className="no-path-info">
          <p>Path tracing will start after the table is filled.</p>
        </div>
      )
    }

    const { type } = pathStepInfo

    if (type === "lcs") {
      const { char, lcsIndex, currentLcs } = pathStepInfo
      return (
        <div className="path-details lcs-found">
          <h3>LCS Character Found!</h3>

          <div className="lcs-char-highlight">{char}</div>
          <p>This character is part of the Longest Common Subsequence!</p>
          <p>
            Position in LCS: {lcsIndex}
            {getOrdinalSuffix(lcsIndex)} character
          </p>
          <div className="lcs-sequence">
            <span>Current LCS: </span>
            {currentLcs.split("").map((c, idx) => (
              <span key={idx} className={`lcs-char ${idx === lcsIndex - 1 ? "current" : ""}`}>
                {c}
              </span>
            ))}
          </div>
          <div className="lcs-info">
            <p>When characters match, we:</p>
            <ol>
              <li>Add the character to our LCS</li>
              <li>Move diagonally (↖️) in the table</li>
            </ol>
          </div>
        </div>
      )
    } else if (type === "move") {
      const { direction, reason } = pathStepInfo
      return (
        <div className="path-details move">
          <h3>Path Tracing Move</h3>
          <div className="move-info">
            <div className="move-direction">
              <span className={`arrow ${direction}`}>{direction === "up" ? "⬆️" : "⬅️"}</span>
              <span>Moving {direction}</span>
            </div>
            <p>Reason: {reason}</p>
          </div>
        </div>
      )
    } else if (type === "complete") {
      const { lcs, length } = pathStepInfo
      return (
        <div className="path-details complete">
          <h3>LCS Complete!</h3>
          <div className="lcs-result-title">Longest Common Subsequence:</div>
          <div className="lcs-result-value">"{lcs}"</div>
          <div className="lcs-result-length">Length: {length}</div>
        </div>
      )
    }

    return null
  }

  return (
    <div className="lcs-page">
      <header className="lcsheader">
        <h1>Longest Common Subsequence (LCS) Visualizer</h1>
        <div className="lcstabs">
          <Button variant="ghost" size="sm" active={activeTab === "visualize"} onClick={() => setActiveTab("visualize")}>Visualize</Button>
          <Button variant="ghost" size="sm" active={activeTab === "learn"} onClick={() => setActiveTab("learn")}>Learn</Button>
          <Button variant="ghost" size="sm" active={activeTab === "code"} onClick={() => setActiveTab("code")}>Code</Button>
        </div>
      </header>

      {activeTab === "visualize" && (
        <div className="lcs-container">
          <div className="lcssidebar left">
            <h2>Input</h2>
            <label>String 1:</label>
            <input type="text" value={str1} onChange={(e) => setStr1(e.target.value)} />
            <label>String 2:</label>
            <input type="text" value={str2} onChange={(e) => setStr2(e.target.value)} />

            <h3>Controls</h3>
            <div className="lcscontrol-buttons">
              <Button variant={isPlaying ? "secondary" : "success"} onClick={handlePlayPause}>
                {isPlaying ? "Pause" : "Play"}
              </Button>
              <Button variant="secondary" size="sm" onClick={resetVisualization}>Reset</Button>
              <Button size="sm" onClick={calculateLCS}>Recalculate</Button>
            </div>

            <h3>Speed</h3>
            <div className="lcsspeed-buttons">
              <Button variant="ghost" size="sm" active={speed === 1000} onClick={() => setSpeedMultiplier(0.5)}>0.5x</Button>
              <Button variant="ghost" size="sm" active={speed === 500} onClick={() => setSpeedMultiplier(1)}>1x</Button>
              <Button variant="ghost" size="sm" active={speed === 250} onClick={() => setSpeedMultiplier(2)}>2x</Button>
            </div>

            <h3>Results</h3>
            <div className="results-box">
              <div className="result-item">
                <span className="result-label">LCS Length:</span>
                <span className="result-value">
                  {matrix.length && matrix[str1.length] ? matrix[str1.length][str2.length] : 0}
                </span>
              </div>
              <div className="result-item">
                <span className="result-label">LCS:</span>
                <span className="lcs-result">{lcsString}</span>
              </div>
            </div>

            <div className="lcslegend">
              <h3>Legend</h3>
              <div>
                <span className="lcslegend-box current-step"></span> Current Step
              </div>
              <div>
                <span className="lcslegend-box path-cell"></span> Path Traced
              </div>
              <div>
                <span className="lcslegend-box lcs-cell"></span> LCS Character
              </div>
              <div>
                <span className="lcslegend-arrow diagonal">↖️</span> Diagonal Move (Match)
              </div>
              <div>
                <span className="lcslegend-arrow up">⬆️</span> Up Move (No Match)
              </div>
              <div>
                <span className="lcslegend-arrow left">⬅️</span> Left Move (No Match)
              </div>
            </div>

            <div className="tip-box">
              <h3>Tip</h3>
              <p>Click on any cell in the table for a detailed explanation!</p>
            </div>
          </div>

          <div className="center-column">
            <div className="table-container" ref={tableContainerRef}>
              <h2>LCS Table</h2>
              <div className="table-wrapper" ref={tableRef}>
                <table>
                  <thead>
                    <tr>
                      <th></th>
                      <th></th>
                      {str2.split("").map((char, idx) => (
                        <th key={idx}>{char}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <th></th>
                      <td className="index-cell">0</td>
                      {Array(str2.length)
                        .fill(0)
                        .map((_, idx) => (
                          <td key={idx} className="index-cell">
                            0
                          </td>
                        ))}
                    </tr>
                    {str1.split("").map((char, i) => (
                      <tr key={i}>
                        <th>{char}</th>
                        <td className="index-cell">0</td>
                        {Array(str2.length)
                          .fill(0)
                          .map((_, j) => {
                            const value =
                              matrix[i + 1] && matrix[i + 1][j + 1] !== undefined ? matrix[i + 1][j + 1] : ""
                            const cellClass = getCellClass(i + 1, j + 1)
                            const isVisible =
                              currentStep > tableSteps.findIndex((step) => step.i === i + 1 && step.j === j + 1)

                            return (
                              <td
                                key={j}
                                className={`cell-${i + 1}-${j + 1} ${cellClass} ${isVisible ? "visible" : "hidden"}`}
                                onClick={(e) => handleCellClick(i + 1, j + 1, e)}
                                title="Click for explanation"
                              >
                                {isVisible ? value : ""}
                              </td>
                            )
                          })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="progress">
                <div>
                  Step: {currentStep} / {tableSteps.length}
                </div>
                <progress value={currentStep} max={tableSteps.length}></progress>
              </div>
            </div>

            <div className="explanation-box">
              <h3>Current Step Explanation</h3>
              <p>{explanation || "Click Play to start the visualization"}</p>
            </div>

            <div className="algorithm-box">
              <h3>LCS Algorithm</h3>
              <div className="formula-display">
                <pre>
                  {`// Main LCS recurrence relation:
if (str1[i-1] === str2[j-1]) {
    // Characters match - take diagonal + 1
    dp[i][j] = dp[i-1][j-1] + 1;
} else {
    // Characters don't match - take max
    dp[i][j] = Math.max(dp[i-1][j], dp[i][j-1]);
}`}
                </pre>
              </div>
            </div>
          </div>

          <div className="lcssidebar right">
            <h2>Step Details</h2>
            <div className="details-container">
              {currentStep > 0 && currentStep <= tableSteps.length ? renderStepDetails() : null}
              {currentStep > tableSteps.length ? renderPathDetails() : null}
            </div>
          </div>
        </div>
      )}

      {activeTab === "learn" && (
        <div className="content-page">
          <h2>Understanding the Longest Common Subsequence</h2>
          <p>
            The Longest Common Subsequence (LCS) problem finds the longest sequence that appears in the same relative
            order within two strings, but not necessarily contiguously.
          </p>

          <div className="animation-container" ref={animationRef}>
            <h3>LCS Animation</h3>
            {renderAnimationFrame()}
            <div className="animation-controls">
              <Button variant="secondary" size="sm" onClick={() => setAnimationFrame((prev) => Math.max(0, prev - 1))}>Previous</Button>
              <span>Step {animationFrame + 1} of 6</span>
              <Button size="sm" onClick={() => setAnimationFrame((prev) => Math.min(5, prev + 1))}>Next</Button>
            </div>
          </div>

          <h3>The Recurrence Relation</h3>
          <div className="code-block">
            <pre>
              {`if (str1[i-1] == str2[j-1])
    dp[i][j] = dp[i-1][j-1] + 1
else
    dp[i][j] = max(dp[i-1][j], dp[i][j-1])`}
            </pre>
          </div>

          <h3>Time and Space Complexity</h3>
          <p>
            <b>Time Complexity:</b> O(m × n) where m and n are the lengths of the two strings
            <br />
            <b>Space Complexity:</b> O(m × n) for the DP table
          </p>
        </div>
      )}

      {activeTab === "code" && (
        <div className="content-page">
          <h2>LCS Algorithm Implementation</h2>
          <div className="code-block">
            <pre>
              {`function longestCommonSubsequence(text1, text2) {
    const m = text1.length;
    const n = text2.length;
    
    // Create DP table
    const dp = Array(m + 1).fill(0).map(() => Array(n + 1).fill(0));
    
    // Fill the dp table
    for (let i = 1; i <= m; i++) {
        for (let j = 1; j <= n; j++) {
            if (text1[i - 1] === text2[j - 1]) {
                dp[i][j] = dp[i - 1][j - 1] + 1;
            } else {
                dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
            }
        }
    }
    
    // Reconstruct the LCS
    let i = m, j = n;
    let lcs = "";
    
    while (i > 0 && j > 0) {
        if (text1[i - 1] === text2[j - 1]) {
            lcs = text1[i - 1] + lcs;
            i--;
            j--;
        } else if (dp[i - 1][j] > dp[i][j - 1]) {
            i--;
        } else {
            j--;
        }
    }
    
    return lcs;
}`}
            </pre>
          </div>

          <h3>Applications of LCS</h3>
          <div className="applications">
            <div className="application-card">
              <h4>Bioinformatics</h4>
              <p>DNA sequence alignment and comparison</p>
            </div>
            <div className="application-card">
              <h4>File Comparison</h4>
              <p>Diff utilities to show changes between files</p>
            </div>
            <div className="application-card">
              <h4>Version Control</h4>
              <p>Git and other VCS use LCS for merging</p>
            </div>
            <div className="application-card">
              <h4>Spell Checking</h4>
              <p>Finding closest matches to misspelled words</p>
            </div>
          </div>
        </div>
      )}

      {showPopup && (
        <div className="popup-overlay" onClick={closePopup}>
          <div
            className="popup"
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "absolute",
              left: `${popupPosition.x}px`,
              top: `${popupPosition.y}px`,
            }}
          >
            <div className="popup-lcsheader">
              <h3>{popupContent.title}</h3>
              <Button variant="ghost" size="sm" onClick={closePopup}>
                ×
              </Button>
            </div>
            <div className="popup-content" dangerouslySetInnerHTML={{ __html: popupContent.content }}></div>
          </div>
        </div>
      )}
    </div>
  )
}

export default LCSVisualizer
